// Shared "needs attention" state for a campaign: unseen notifications (item
// refunds, reward grants) and claimable streak bonuses. One provider keeps the
// NavBar badge, the basecamp HubBar, and the inventory modal in sync — when one
// screen clears notifications or claims a streak, the others update.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { NOTIFICATIONS_CHANGED_EVENT, emitNotificationsChanged } from './notificationsEvents';

import { apiFetch } from '../api/client';

const EMPTY = { unseenCount: 0, unseen: [], accoladeCount: 0, streak: null, streakClaimableCoins: 0 };

// Re-export so existing imports from this module keep working.
export { NOTIFICATIONS_CHANGED_EVENT, emitNotificationsChanged };

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [byCampaign, setByCampaign] = useState({});

  const post = useCallback((path, campaignId, extra = {}) => (
    apiFetch(`${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
      body: JSON.stringify({ campaign_id: Number(campaignId), ...extra }),
    })
  ), []);

  const refresh = useCallback(async (campaignId) => {
    if (!campaignId || !token || !isAuthenticated) return;
    try {
      const res = await post('/api/campaign/notifications', campaignId);
      if (!res.ok) return;
      const data = await res.json();
      setByCampaign((prev) => ({
        ...prev,
        [campaignId]: {
          unseenCount: data?.unseen_count || 0,
          unseen: Array.isArray(data?.unseen) ? data.unseen : [],
          accoladeCount: data?.accolade_count || 0,
          streak: data?.streak || null,
          streakClaimableCoins: data?.streak?.claimable_coins || 0,
        },
      }));
    } catch {
      /* best-effort */
    }
  }, [post, token, isAuthenticated]);

  // Attention counts for ALL of the user's campaigns, so the nav can badge each
  // campaign and aggregate for the main Basecamp button. Merges into byCampaign
  // without clobbering any per-campaign detail (the unseen list / streak object).
  const refreshSummary = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const res = await apiFetch(`/api/notifications/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({}),
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.campaigns) ? data.campaigns : [];
      setByCampaign((prev) => {
        const next = { ...prev };
        list.forEach((c) => {
          const key = c.campaign_id;
          next[key] = {
            ...(next[key] || EMPTY),
            unseenCount: c.unseen_count || 0,
            accoladeCount: c.accolade_count || 0,
            streakClaimableCoins: c.streak_claimable_coins || 0,
          };
        });
        return next;
      });
    } catch {
      /* best-effort */
    }
  }, [token, isAuthenticated]);

  // Re-pull the all-campaign summary when notifications change out-of-band or
  // the window regains focus (so the nightly job / admin seeds show up live).
  useEffect(() => {
    const handler = () => refreshSummary();
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, handler);
    window.addEventListener('focus', handler);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, handler);
      window.removeEventListener('focus', handler);
    };
  }, [refreshSummary]);

  const markSeen = useCallback(async (campaignId, ids = null) => {
    if (!campaignId || !token) return;
    try {
      await post('/api/campaign/notifications/seen', campaignId, ids ? { ids } : {});
    } catch {
      /* best-effort */
    }
    refresh(campaignId);
  }, [post, token, refresh]);

  const claimStreak = useCallback(async (campaignId) => {
    if (!campaignId || !token) return null;
    try {
      const res = await post('/api/campaign/streak/claim', campaignId);
      const data = res.ok ? await res.json() : null;
      refresh(campaignId);
      return data;
    } catch {
      return null;
    }
  }, [post, token, refresh]);

  return (
    <NotificationsContext.Provider value={{ byCampaign, refresh, refreshSummary, markSeen, claimStreak }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext) || {
    byCampaign: {},
    refresh: () => {},
    refreshSummary: () => {},
    markSeen: () => {},
    claimStreak: () => null,
  };
}

// True if ANY of the user's campaigns currently needs attention.
export function useAnyAttention() {
  const { byCampaign } = useNotifications();
  return Object.values(byCampaign).some(
    (a) => (a?.unseenCount || 0) > 0 || (a?.accoladeCount || 0) > 0 || (a?.streakClaimableCoins || 0) > 0
  );
}

export function useCampaignAttention(campaignId) {
  const { byCampaign } = useNotifications();
  return (campaignId != null && byCampaign[campaignId]) || EMPTY;
}
